export interface ChatEntry {
  opener: string
  starterPrompts: string[]
}

export const DEFAULT_CHAT: ChatEntry = {
  opener: 'Hi — how can I help?',
  starterPrompts: ['What can you do?', "Show me today's tasks", 'Draft an email'],
}

export const chatContent: Record<string, ChatEntry> = {
  voice: {
    opener: 'Hi — voice view ready, want me to spin up a session or summarise the last one?',
    starterPrompts: [
      'Summarise my last voice session',
      'Start a new session',
      'What did I capture yesterday?',
    ],
  },
  chat: {
    opener: 'Hi — chat is open, ask me anything or tap a prompt to get started.',
    starterPrompts: [
      'What can you do?',
      'Summarise my morning',
      'Draft a quick reply',
    ],
  },
  tasks: {
    opener: 'Hi — Tasks open, want me to send one or break a big one down?',
    starterPrompts: [
      'What should I do first?',
      'Break this task down',
      'Send the top card',
    ],
  },
  actions: {
    opener: 'Hi — Actions queue open, want me to check what is running or stalled?',
    starterPrompts: [
      'What is running right now?',
      'Anything stuck in the queue?',
      'Show me the last finished action',
    ],
  },
  artifacts: {
    opener: 'Hi — Artifacts grid open, want me to surface anything waiting for approval?',
    starterPrompts: [
      'What needs my approval?',
      'Show recent drafts',
      'Filter to rejected artifacts',
    ],
  },
  transcripts: {
    opener: 'Hi — Transcripts open, want me to search past sessions for something?',
    starterPrompts: [
      'Find where I mentioned Acme',
      'Summarise last week of sessions',
      'Show my longest session',
    ],
  },
  lists: {
    opener: 'Hi — Lists view open, want to build a computed list or browse the core ones?',
    starterPrompts: [
      'Create a new computed list',
      'Show me standard lists',
      'What is in my embeds tab?',
    ],
  },
  summaries: {
    opener: 'Hi — Summaries open, want me to compose a new one or refresh an existing doc?',
    starterPrompts: [
      'Refresh my daily summary',
      'Compose a project summary',
      'Which summaries are stale?',
    ],
  },
  documents: {
    opener: 'Hi — Documents open, want me to draft a section or review recent comments?',
    starterPrompts: [
      'Draft a new section',
      'Show unresolved comments',
      'Compare the last two versions',
    ],
  },
  library: {
    opener: 'Hi — Library open, want me to search Confluence or GDrive for something?',
    starterPrompts: [
      'Find the architecture doc',
      'What synced from GDrive today?',
      'Show me the latest labels',
    ],
  },
  data: {
    opener: 'Hi — Data open, want me to pull Jira, QuickBooks or Calendar records?',
    starterPrompts: [
      'Show open Jira issues',
      'What is on my calendar tomorrow?',
      'Latest QuickBooks invoices',
    ],
  },
  messages: {
    opener: 'Hi — Messages open, want me to triage Gmail and Slack for you?',
    starterPrompts: [
      'What needs a reply today?',
      'Summarise unread Slack',
      'Draft a reply to the top email',
    ],
  },
  feed: {
    opener: 'Hi — Feed open, want a digest of YouTube and Reddit activity?',
    starterPrompts: [
      'Summarise today’s feed',
      'Top YouTube picks this week',
      'Interesting Reddit threads',
    ],
  },
  browser: {
    opener: 'Hi — Browser open, want me to pull up a bookmark or summarise a page?',
    starterPrompts: [
      'Open my bookmarks',
      'Summarise the current page',
      'Save this tab for later',
    ],
  },
  agents: {
    opener: 'Hi — Agents open, want to review a profile or check recent sessions?',
    starterPrompts: [
      'Show my agent profiles',
      'What did my agents do today?',
      'Check the soul of my main agent',
    ],
  },
  workflows: {
    opener: 'Hi — Workflows open, want to build a new one or review run history?',
    starterPrompts: [
      'Show recent workflow runs',
      'Build a new workflow',
      'Which workflow failed last?',
    ],
  },
  'source-rules': {
    opener: 'Hi — Source Rules open, want to tweak a rule and deploy it back?',
    starterPrompts: [
      'Create a new sender rule',
      'Show deployed Gmail filters',
      'Why was this email tagged?',
    ],
  },
  skills: {
    opener: 'Hi — Skills open, want to install something new or see what is active?',
    starterPrompts: [
      'What skills do I have installed?',
      'Install a new skill',
      'Disable an unused skill',
    ],
  },
  settings: {
    opener: 'Hi — Settings open, want to change voice, model or sync preferences?',
    starterPrompts: [
      'Switch voice model',
      'Adjust notifications',
      'Review data sync settings',
    ],
  },
  'sync-history': {
    opener: 'Hi — Sync History open, want me to check if anything failed overnight?',
    starterPrompts: [
      'What failed in the last 24h?',
      'Did Confluence sync on schedule?',
      'Summarise new Jira issues',
    ],
  },
}

export const routeDisplayNames: Record<string, string> = {
  voice: 'Voice',
  chat: 'Chat',
  tasks: 'Tasks',
  actions: 'Actions',
  artifacts: 'Artifacts',
  transcripts: 'Transcripts',
  lists: 'Lists',
  summaries: 'Summaries',
  documents: 'Documents',
  library: 'Library',
  data: 'Data',
  messages: 'Messages',
  feed: 'Feed',
  browser: 'Browser',
  agents: 'Agents',
  workflows: 'Workflows',
  'source-rules': 'Source Rules',
  skills: 'Skills',
  settings: 'Settings',
  'sync-history': 'Sync History',
}
